import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import {
  MyLeadsFilterTypes,
  MyLeadsSearchQuery,
} from '@mm/state/interfaces/myLeads';
import { ConfigProvider, Radio, RadioChangeEvent } from 'antd';
import { isEqual } from 'lodash';
import React, { useLayoutEffect, useState } from 'react';
import styles from '../MyLeadsFiltersPanel.module.css';
import { useFilterParams } from '../use-filter-params';

type Props = {
  attributeKey: string;
  title: string;
  filterParams: MyLeadsSearchQuery;
  setFilterParams: (params: MyLeadsSearchQuery) => void;
  includeName: string;
  excludeName: string;
  ignoreName: string;
};

type BoolObjectFilter = { filter: boolean; allow_nil?: boolean } | boolean;

export const RadioTernary = ({
  attributeKey,
  title,
  filterParams,
  setFilterParams,
  includeName,
  excludeName,
  ignoreName,
}: Props) => {
  const defaultOptions = [
    { name: includeName, value: 'include' },
    { name: excludeName, value: 'exclude' },
  ];
  const values = {
    include: true,
    exclude: { filter: false, allow_nil: true },
    ignore: 'ignore',
  };
  const currentValue: any[] = filterParams.query?.attributes?.[attributeKey];
  const currentVal = currentValue?.[0];

  const defaultGroupValue =
    Object.keys(values).find(key => isEqual(values[key], currentVal)) ||
    ('ignore' as any);

  const { getUpdatedFilterParams, shouldDisableFilters } = useFilterParams({
    filterParams,
  });

  const { primaryColor, isWhitelabelCompany } = useWhitelabelConfig();

  const [groupValue, setGroupValue] = useState<
    'include' | 'exclude' | 'ignore'
  >(defaultGroupValue);

  const updateFilterParams = (group: string) => {
    if (currentValue) {
      delete filterParams.query?.attributes?.[attributeKey];
    }

    const updatedQuery = getUpdatedFilterParams({
      key: attributeKey,
      value: values[group],
      isExisting: group === 'ignore',
      type: MyLeadsFilterTypes.attributes,
    });
    const newValue = updatedQuery?.query?.attributes?.[attributeKey];

    if (currentValue !== newValue) {
      setFilterParams(updatedQuery);
    }
  };

  const onRadioChange = (e: RadioChangeEvent) => {
    const { value } = e.target || {};
    setGroupValue(value);
    updateFilterParams(value);
  };

  useLayoutEffect(() => {
    setGroupValue(defaultGroupValue);
  }, [filterParams.query?.attributes]);

  return (
    <div className={styles.radioGroupWrapper}>
      <div className={styles.heading}>{title}</div>

      <Radio.Group onChange={onRadioChange} value={groupValue}>
        {defaultOptions.map(option => {
          const isRadioChecked = isEqual(groupValue, option.value);
          return (
            <div
              className={styles.radioGroupRow}
              key={`${attributeKey}-${option.value}`}
            >
              <ConfigProvider
                theme={{
                  components: {
                    Radio: {
                      colorPrimary: isWhitelabelCompany
                        ? primaryColor
                        : 'var(--color-geekblue-6)',
                    },
                  },
                }}
              >
                <Radio
                  defaultChecked={isRadioChecked}
                  value={option.value}
                  className={styles.radio}
                  disabled={shouldDisableFilters}
                >
                  {option.name}
                </Radio>
              </ConfigProvider>
            </div>
          );
        })}
        <div className={styles.radioGroupRow} key={`${attributeKey}-ignore`}>
          <ConfigProvider
            theme={{
              components: {
                Radio: {
                  colorPrimary: isWhitelabelCompany
                    ? primaryColor
                    : 'var(--color-geekblue-6)',
                },
              },
            }}
          >
            <Radio
              defaultChecked={groupValue === 'ignore'}
              value="ignore"
              className={styles.radio}
              disabled={shouldDisableFilters}
            >
              {ignoreName}
            </Radio>
          </ConfigProvider>
        </div>
      </Radio.Group>
    </div>
  );
};
